import React from 'react'
import type { CSSProperties } from 'react'
import { Button } from 'react-bootstrap'
import { useDrop } from 'react-dnd'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Download } from 'react-bootstrap-icons';
import { BuyNFT, ListNFT, ResellNFT, UnlistNFT } from '../../pages/nfts/store/nfts.actions'
import { Voucher } from '../../pages/nfts/store/nfts.reducer'
import { Web3ContextType } from '../../providers/context.types'
import { Web3Context } from '../../providers/web3.context'
import { AppState } from '../../store/root.reducer'
import { ethToWei, getSignature } from '../../utils/signMessage'
import User from '../../models/user.model'
import { downloadCoA, downloadFile } from '../../utils/apiRequest'

const style: CSSProperties = {
    width: '100%',
    height: '400px',
    // backgroundColor: 'ActiveBorder'
}

export const NftBoard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const user = useSelector((state: AppState) => state.auth.user as User);
    const { selectedNFT, slot, isEmpty, nftError } = useSelector((state: AppState) => state.nft);
    const { web3, address, marketplaceContract } = React.useContext(Web3Context) as Web3ContextType;

    const [ pending, setPending ] = React.useState<boolean>(false);
    const [ newPrice, setNewPrice ] = React.useState<string>();

    let isOwner = false;
    if(selectedNFT && user) {
        isOwner = (selectedNFT as Voucher).owner === user.wallet;
    }

    const [{canDrop, isOver}, drop] = useDrop(() => ({
        accept: 'NFT',
        drop: ({tokenId}) => {
            // find token from wallet 
        },  
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop()
        })
    }))

    const listHandler = () => {
        if(!selectedNFT || !user) return;

        dispatch(ListNFT());
    }

    const unlistActions = () => {
        if(!user) return;
        dispatch(UnlistNFT());
    }

    const buyHandler = async () => {
        if(!user) return;
        try {
            setPending(true);
            const gasPrice = await web3.eth.getGasPrice();
            const receipt: any = await marketplaceContract.methods.redeem(address, {
                tokenId: selectedNFT.tokenId,
                price: selectedNFT.price.toString(),
                royaltyRate: selectedNFT.royalty,
                uri: selectedNFT.tokenUri,
                resell: selectedNFT.resell
            }, selectedNFT.signature).send({
                from: user.wallet,
                gasPrice: gasPrice.toString(),
                value: selectedNFT.price,
                maxPriorityFeePerGas: null,
                maxFeePerGas: null
            });

            const { transactionHash, status } = receipt;
            if(status) {
                dispatch(BuyNFT({hash: transactionHash, newOwner: user.wallet}));
            }
        } catch (error) {
            console.log(error); 
        } finally {
            setPending(false);
        }
    }

    const resellHandler = async () => {
        if(!newPrice || !slot || !user) return;

        const newPriceInWei = ethToWei(web3, +newPrice);
        const _selectedNft = selectedNFT as Voucher;
        const voucher = {
            tokenId: _selectedNft.tokenId,
            uri: _selectedNft.tokenUri,
            price: newPriceInWei,
            royaltyRate: _selectedNft.royalty,
            resell: true,
            artType: _selectedNft.artType,
            metadata: _selectedNft.metadata
        }        

        const {message, signature} = await getSignature(web3, user.wallet, voucher);
        // lazy mint api request
        dispatch(ResellNFT({
            name: _selectedNft.name,
            description: _selectedNft.description,
            tokenId: voucher.tokenId,
            tokenUri: voucher.uri,
            price: newPriceInWei.toString(),
            royalty: voucher.royaltyRate,
            signature: signature.result,
            message,
            creator: _selectedNft.creator,
            owner: user.wallet,
            resell: true,
            position: slot,
            artType: voucher.artType,
            metadata: voucher.metadata
        }));
    }

    const download = async (key: string) => {
        await downloadFile(key);
    }

    const _downloadCoA = async (voucherId: string) => {
        await downloadCoA(voucherId);
    }
    
    return (
        <>
            <div className="row">
                <div className="col-md-8">
                    <div ref={drop} style={{...style}} data-testid="NFT">
                        {selectedNFT ? 
                        <>
                            <img style={{width: 'inherit', height: 'inherit'}} src={selectedNFT.tokenUri} alt='selected token' />
                        </> : 
                        <>
                            <img src='/nfts/nftbackground.png' alt='background' />
                        </>}
                    </div>
                    <div>
                        {selectedNFT ? 
                            <div className='mt-4'>
                                <div className="row">
                                    <div className="col-md-3">
                                        <p className='fw-bold'>Name</p>
                                    </div>
                                    <div className="col-md-6">
                                        <p>{(selectedNFT as Voucher).name}</p>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-3">
                                        <p className='fw-bold'>Description</p>
                                    </div>
                                    <div className="col-md-6">
                                    <p>{(selectedNFT as Voucher).description}</p>
                                    </div>
                                </div>
                                {(selectedNFT as Voucher).price ? <div className="row">
                                    <div className="col-md-3">
                                        <p className='fw-bold'>Price</p>
                                    </div>
                                    <div className="col-md-6">
                                        <p>{web3.utils.fromWei((selectedNFT as Voucher).price.toString(), 'ether')} ETH</p>
                                    </div>
                                </div> : <></>}
                                <div className="row">
                                    <div className="col-md-3">
                                        <p className='fw-bold'>Royalty</p>
                                    </div>
                                    <div className="col-md-6">
                                        <p>{(selectedNFT as Voucher).royalty / 100} (%)</p>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-3">
                                        <p className='fw-bold'>Owner</p>
                                    </div>
                                    <div className="col-md-6">
                                        <p>{(selectedNFT as Voucher).owner}</p>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-3">
                                        <p className='fw-bold'>Creator</p>
                                    </div>
                                    <div className="col-md-6">
                                        <p>{(selectedNFT as Voucher).creator}</p>
                                    </div>
                                </div>
                                {(selectedNFT as Voucher).attachments && <>
                                    {(selectedNFT as Voucher).attachments.map((attachment, i) => {
                                        return (
                                            <div key={i} className="row mb-1">
                                            <div className="col-7"><span className="me-3">{attachment.name}</span></div>
                                            <div className="col-3">
                                                <span style={{cursor: "pointer"}}><Download onClick={() => download(attachment.url)} /></span>
                                            </div>
                                            </div>
                                        )
                                    })}
                                </>}
                                {(selectedNFT as Voucher).metadata && <>
                                    {/* show Physical art metadata */}
                                    <div className="row">
                                        <div className="col-md-3">
                                            <p className="fw-bold">Artist Name</p>
                                        </div>
                                        <div className="col-md-6">
                                            <p>{selectedNFT.metadata.artistName}</p>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-3">
                                            <p className="fw-bold">Title</p>
                                        </div>
                                        <div className="col-md-6">
                                            <p>{selectedNFT.metadata.title}</p>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-3">
                                            <p className="fw-bold">Format</p>
                                        </div>
                                        <div className="col-md-6">
                                            <p>{selectedNFT.metadata.format}</p>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-3">
                                            <p className="fw-bold">Materials</p>
                                        </div>
                                        <div className="col-md-6">
                                            <p>{selectedNFT.metadata.materials}</p>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-3">
                                            <p className="fw-bold">Date of Creation</p>
                                        </div>
                                        <div className="col-md-6">
                                            <p>{selectedNFT.metadata.dateOfCreation}</p>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-3">
                                            <Button
                                                onClick={() => _downloadCoA(selectedNFT._id)}
                                            >Download CoA</Button>
                                        </div>
                                    </div>
                                </>}
                                
                                
                            </div>: null
                        }
                    </div>
                </div>
                <div className="col-md-4">
                    {
                    isEmpty ? 
                        // it is the resell or listing case
                        <>
                            {selectedNFT ? 
                                <>
                                    {
                                        selectedNFT.price ? 
                                            // for list case from voucher!
                                            <>
                                                <Button 
                                                    onClick={listHandler}
                                                    disabled={!user}
                                                >List</Button>
                                            </> : 
                                            // for resell case
                                            <>
                                                <div className='mb-4'>
                                                    <label>New price</label>
                                                    <input 
                                                        type='text' 
                                                        className='form-control'
                                                        value={newPrice}
                                                        onChange={e => setNewPrice(e.target.value)}
                                                    />
                                                </div>
                                                <Button 
                                                    onClick={resellHandler} 
                                                    className="me-3"
                                                    disabled={!user}
                                                >Resell</Button>
                                            </>
                                    }
                                </> : null
                            }
                        </> :
                        // buy or update price case
                        <>
                            {isOwner ? 
                                // update price of voucher that is already listed
                                <>
                                    <Button 
                                        onClick={unlistActions} 
                                        className="me-3" 
                                        disabled={!user}
                                    >Unlist</Button>
                                </> : 
                                // buy selected NFT!
                                <>
                                    <Button 
                                        onClick={buyHandler}
                                        disabled={!user}
                                    >Buy</Button>
                                </>
                            }
                        </>
                    }
                    <Button onClick={() => navigate('/exhibitions/hexa')}>Go back</Button>
                </div>
            </div>
        </>
    )
}